// Services
import addressFinderAutocompleteService from "@/app/services/address-finder/address-finder-autocomplete/address-finder-autocomplete-service.js";
import walkerSitterService from "@/app/services/main/walker-sitter/walker-sitter-service.js";

// Mixins
import { toastMessageMixins } from "@/app/mixins/utils/toast-message-mixins";
import { inputTextMixins } from "@/app/mixins/utils/input-text-mixins";
import { userMixins } from "@/app/mixins/modules/user-mixins.js";
import { switchPetMixins } from "@/app/mixins/modules/switch-pet-mixins";
import { walkerSitterMixins } from "@/app/mixins/components/walker-sitter-mixins";

// Constants
import { STATUS_CODES } from "@/app/constants/status-constants";

// Third party libraries
import DatePicker from "vue2-datepicker";

// Components
const walkerSitterModal = () =>
    import(
        /* webpackPrefetch: true, webpackChunkName: "walker-sitter-modal" */ "@/app/views/main-view/walker-sitter/components/modals/walker-sitter-modal/walker-sitter-modal.vue"
    );

export default {
    name: "WalkerSitter",
    components: { DatePicker, walkerSitterModal },
    mixins: [inputTextMixins, toastMessageMixins, userMixins, switchPetMixins, walkerSitterMixins],
    data() {
        return {
            selectedRole: "SITT",
            selectedAddress: "",
            roleList: [
                {
                    value: "SITT",
                    name: "Sitter",
                },
                {
                    value: "WALK",
                    name: "Walker",
                },
                {
                    value: "WASI",
                    name: "Walker and Sitter",
                },
            ],
            addressList: [],
            isAddressLoading: false,
            distance: 20,
            date: "",
            isAPILoading: false,
            isLoadMoreLoading: false,
            walkerSitterList: [],
            showNoResult: false,
            isSearchDisabled: true,
            latitude: "",
            longitude: "",
            noOfDays: "",
            page: 1,
            limitFilter: 6,
            increment: 6,
            modalContent: {},
            role: {
                WASI: "Walker and Sitter",
                WALK: "Walker",
                SITT: "Sitter",
            },
            initialLoad: true,
        };
    },
    async created() {
        const { addressSearch, latSearch, longSearch } = this.getAddressSearch;

        if (addressSearch && latSearch && longSearch) {
            this.selectedAddress = JSON.parse(`{"full_address": "address", "displayAddress": "${addressSearch}"}`);
            this.latitude = latSearch;
            this.longitude = longSearch;

            // On load add the favorites
            await this.walkerSitterListApiCall(false, true, true);
        }
    },
    watch: {
        selectedAddress(newVal) {
            if (newVal) {
                this.isSearchDisabled = !(newVal && newVal.full_address);
            } else {
                this.isSearchDisabled = true;
            }
        },
    },
    methods: {
        async refreshActivePetData() {
            await this.walkerSitterListApiCall(false, true, true);
        },
        findAddress(address) {
            if (address) {
                this.isAddressLoading = true;

                this.delayTyping(() => {
                    this.getAutocompleteAddress(address);
                });
            }
        },
        getAutocompleteAddress(address) {
            addressFinderAutocompleteService
                .getAutocompleteAddress(address)
                .then((res) => {
                    if (res.status === STATUS_CODES.SUCCESS) {
                        this.addressList = res.data.completions.map((add) => {
                            let tempAddress = add.full_address.split(" ");
                            add.displayAddress = `${tempAddress[tempAddress.length - 2]} ${
                                tempAddress[tempAddress.length - 1]
                            }`;
                            return add;
                        });
                        this.isAddressLoading = false;
                    } else {
                        const { ERROR_GET_ADDRESS } = this.toastMessages.addressFinder();
                        this.showAPIErrorToast(ERROR_GET_ADDRESS, res);
                        this.isAddressLoading = false;
                    }
                })
                .catch(() => {
                    this.showGenericErrorToast();
                    this.isAddressLoading = false;
                });
        },
        async getAddressMetaData(address) {
            await addressFinderAutocompleteService
                .getAddressMetaData(address.id)
                .then((res) => {
                    if (res.status === STATUS_CODES.SUCCESS) {
                        const { latitude, longitude } = res.data;

                        this.latitude = latitude;
                        this.longitude = longitude;
                        this.walkerSitterListApiCall(false, true);
                    } else {
                        const { ERROR_GET_ADDRESS } = this.toastMessages.addressFinder();
                        this.showAPIErrorToast(ERROR_GET_ADDRESS, res);
                        this.isAddressLoading = false;
                    }
                })
                .catch(() => {
                    this.showGenericErrorToast();
                    this.isAddressLoading = false;
                });
        },
        notBeforeToday(date) {
            return date < new Date();
        },
        getDisplayAddress(address) {
            if (address) {
                let tempAddress = address.split(" ");
                return `${tempAddress[tempAddress.length - 2]} ${tempAddress[tempAddress.length - 1]}`;
            } else {
                return "no address";
            }
        },
        mobileNumberFormat(mobileNumber) {
            if (mobileNumber) {
                let data = mobileNumber.split("");
                data.splice(4, 0, "-");
                return data.join("");
            } else {
                return "";
            }
        },
        async walkerSitterListApiCall(isLoadMore, isFilter, getAllFavorites = false) {
            if (this.selectedAddress && !this.selectedAddress.full_address) {
                return;
            }

            this.initialLoad = false;

            if (isFilter) {
                this.isAPILoading = true;
                this.page = 1;
                this.limitFilter = 6;
            }

            let queryParams = {
                page: this.page,
                limit: this.increment,
                f_role: this.selectedRole,
                f_latitude: this.latitude,
                f_longitude: this.longitude,
                f_breedId: this.getActivePet.breed.id,
                f_searchAddress: this.selectedAddress.displayAddress,
                f_petId: this.getActivePet.id,
            };

            if (this.distance) {
                queryParams["f_distance"] = this.distance;
            }

            if (this.date) {
                queryParams["f_date"] = this.date;
            }

            if (this.selectedRole !== "WALK" && this.noOfDays) {
                queryParams["f_noOfDays"] = this.noOfDays;
            }

            if (getAllFavorites) {
                queryParams["f_getAllFavorite"] = "true";
            }

            let userDetails = this.userDetails;

            userDetails.searchAddress = this.selectedAddress.displayAddress;
            userDetails.latSearch = this.latitude;
            userDetails.longSearch = this.longitude;

            this.$store.commit("setUserDetails", userDetails);

            await walkerSitterService.getWalkerSitterList(queryParams).then((res) => {
                if (res.status === STATUS_CODES.SUCCESS) {
                    if (isLoadMore) {
                        this.walkerSitterList.push(...res.data.data);
                    } else {
                        this.walkerSitterList = res.data.data;
                    }

                    this.isAPILoading = false;
                    this.isLoadMoreLoading = false;

                    if (res.data.pagination.totalCount && res.data.pagination.totalCount > this.limitFilter) {
                        this.showNoResult = false;
                    } else {
                        this.showNoResult = true;
                    }
                } else {
                    this.showAPIErrorToast("Unable to fetch product list.", res);
                    this.isAPILoading = false;
                    this.isLoadMoreLoading = false;
                }
            });
        },
        async filterWalkerSitter() {
            if (this.selectedAddress.id) {
                await this.getAddressMetaData(this.selectedAddress);
            } else {
                await this.walkerSitterListApiCall(false, true);
            }
        },
        loadMore() {
            this.isLoadMoreLoading = true;
            this.page++;
            this.limitFilter += this.increment;
            this.walkerSitterListApiCall(true, false);
        },
        openWalkerSitterModal(modalContent) {
            this.modalContent = modalContent;
            this.$refs.modalWalkerSitter.show();
        },
        showEmail(email) {
            if (email) {
                window.location.href = `mailto:${email}`;
            }
        },
        callBackSaveFavorite(list) {
            if (list) {
                const index = this.walkerSitterList.findIndex((item) => item.id === list.id);
                if (index !== -1) {
                    this.walkerSitterList[index].favorite = !this.walkerSitterList[index].favorite;
                }
            }
        },
        setSelectedAddress(e) {
            this.selectedAddress = e;
        },
        getImage(src) {
            return src || require("@/assets/images/_shared/default-user-pic.png");
        },
    },
};
